import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

/**
 * Liste de choix (combobox « select-only » du guide ARIA), sur le modèle du
 * CustomSelect de LizPack. Le menu est placé en CSS sous son bouton, sans
 * calcul de position en JavaScript : il reste juste quand la page est
 * agrandie par `zoom` (écrans 2K et 4K, index.css).
 *
 * Clavier : flèches, Début, Fin, Entrée ou Espace pour choisir, Échap pour
 * fermer ; un appui hors de la liste la ferme.
 */
export const ListeChoix = ({
  id,
  options,
  valeur,
  onChange,
  placeholder = "Choisir",
  disabled = false,
  ...aria
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const racine = useRef(null);
  const bouton = useRef(null);
  const idListe = `${id}-options`;
  const choisi = options.find((o) => o.value === valeur);

  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e) => {
      if (!racine.current?.contains(e.target)) setOuvert(false);
    };
    document.addEventListener("pointerdown", dehors);
    return () => document.removeEventListener("pointerdown", dehors);
  }, [ouvert]);

  const ouvrir = () => {
    const i = options.findIndex((o) => o.value === valeur);
    setActif(i >= 0 ? i : 0);
    setOuvert(true);
  };

  const choisir = (i) => {
    onChange(options[i].value);
    setOuvert(false);
    bouton.current?.focus();
  };

  const surTouche = (e) => {
    if (disabled) return;
    if (!ouvert) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        ouvrir();
      }
      return;
    }
    const dernier = options.length - 1;
    const actions = {
      ArrowDown: () => setActif((i) => Math.min(i + 1, dernier)),
      ArrowUp: () => setActif((i) => Math.max(i - 1, 0)),
      Home: () => setActif(0),
      End: () => setActif(dernier),
      Enter: () => choisir(actif),
      " ": () => choisir(actif),
      Escape: () => setOuvert(false),
    };
    if (e.key === "Tab") {
      setOuvert(false);
      return;
    }
    if (actions[e.key]) {
      e.preventDefault();
      actions[e.key]();
    }
  };

  return (
    <div ref={racine} className="relative">
      <button
        ref={bouton}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={ouvert}
        aria-controls={idListe}
        aria-activedescendant={ouvert ? `${idListe}-${actif}` : undefined}
        disabled={disabled}
        onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
        onKeyDown={surTouche}
        className={`flex h-12 w-full items-center justify-between rounded-[8px] border bg-white px-3 text-left text-vt-texte font-medium focus-visible:outline-none disabled:cursor-not-allowed ${
          ouvert ? "border-brand" : "border-input focus-visible:border-brand"
        } ${choisi ? "text-foreground" : "text-t2"}`}
        {...aria}
      >
        {choisi ? choisi.label : placeholder}
        <ChevronDown
          className={`h-4 w-4 flex-none text-t2 ${ouvert ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      <ul
        id={idListe}
        role="listbox"
        aria-labelledby={`${id}-libelle`}
        hidden={!ouvert}
        className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto overscroll-contain rounded-[10px] border border-border bg-white p-1 shadow-md"
      >
        {options.map((option, i) => (
          <li
            key={option.value}
            id={`${idListe}-${i}`}
            role="option"
            aria-selected={option.value === valeur}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => choisir(i)}
            onPointerMove={() => setActif(i)}
            className={`flex cursor-pointer items-center justify-between rounded-[6px] py-2.5 pl-3 pr-3 text-vt-texte font-medium ${
              i === actif ? "bg-bg2" : ""
            }`}
          >
            {option.label}
            {option.value === valeur && (
              <Check className="h-4 w-4 flex-none text-accent" aria-hidden="true" />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
